import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

// Cloudflare Turnstile captcha. Only active when REACT_APP_TURNSTILE_SITE_KEY is set at build time;
// without it this renders nothing and signup works exactly as before (see CLOUDFLARE.md).
export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let loader = null;
const loadScript = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loader) {
    loader = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => { loader = null; reject(new Error("captcha script failed to load")); };
      document.head.appendChild(s);
    });
  }
  return loader;
};

// onToken(token) fires with the solved token, or "" when it expires / errors.
// ref.current.reset() asks for a fresh challenge (tokens are single-use, so call it after every attempt).
const Turnstile = forwardRef(function Turnstile({ onToken }, ref) {
  const box = useRef(null);
  const widgetId = useRef(null);
  const callback = useRef(onToken);
  const [failed, setFailed] = useState(false);

  useEffect(() => { callback.current = onToken; }, [onToken]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;
    loadScript()
      .then((ts) => {
        if (cancelled || !box.current) return;
        widgetId.current = ts.render(box.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          callback: (token) => callback.current(token),
          "expired-callback": () => callback.current(""),
          "error-callback": () => callback.current(""),
        });
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch {}
      }
      widgetId.current = null;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    reset: () => {
      callback.current("");
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.reset(widgetId.current); } catch {}
      }
    },
  }), []);

  if (!TURNSTILE_SITE_KEY) return null;
  return (
    <>
      <div ref={box} className="turnstile-box" />
      {failed && <small className="turnstile-error">Couldn't load the captcha. Check your connection or ad blocker, then reload the page.</small>}
    </>
  );
});

export default Turnstile;
